import {
  init,
  captureMessage,
  browserTracingIntegration,
  replayIntegration,
  type BrowserOptions,
  type ErrorEvent,
  type EventHint,
} from '@sentry/browser';

// Build-time constants injected by vite.config.ts `define`.
// Kept as dedicated sentinels (not `import.meta.env.VITE_*`) because vite's
// define cannot reliably rewrite import.meta.env keys — they go through
// vite's own env pipeline that only reads .env files, not process.env.
declare const __SENTRY_RELEASE__: string;
declare const __SENTRY_DIST__: string;

type InitResult = 'initialized' | 'skipped-no-dsn';

const PII_PATTERNS: Array<{ name: string; regex: RegExp; replace: string }> = [
  { name: 'email', regex: /[\w.+-]+@[\w-]+\.[\w.-]+/g, replace: '[email]' },
  { name: 'jwt', regex: /eyJ[\w-]+\.[\w-]+\.[\w-]+/g, replace: '[jwt]' },
  { name: 'phone-kr', regex: /\b01[016789][\s-]?\d{3,4}[\s-]?\d{4}\b/g, replace: '[phone]' },
];

export function scrubPII(input: string): string {
  return PII_PATTERNS.reduce((acc, { regex, replace }) => acc.replace(regex, replace), input);
}

function beforeSend(event: ErrorEvent, _hint: EventHint): ErrorEvent | null {
  if (event.request?.url) event.request.url = scrubPII(event.request.url);
  if (event.request?.query_string && typeof event.request.query_string === 'string') {
    event.request.query_string = scrubPII(event.request.query_string);
  }
  if (event.message) event.message = scrubPII(event.message);
  if (event.exception?.values) {
    for (const ex of event.exception.values) {
      if (ex.value) ex.value = scrubPII(ex.value);
    }
  }
  if (event.breadcrumbs) {
    for (const crumb of event.breadcrumbs) {
      if (typeof crumb.message === 'string') crumb.message = scrubPII(crumb.message);
      if (crumb.data && typeof crumb.data === 'object') {
        for (const key of Object.keys(crumb.data)) {
          const value = crumb.data[key];
          if (typeof value === 'string') crumb.data[key] = scrubPII(value);
        }
      }
    }
  }
  return event;
}

export function initSentry(): InitResult {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) {
    // eslint-disable-next-line no-console
    console.warn('[sentry] VITE_SENTRY_DSN not set — Sentry disabled');
    return 'skipped-no-dsn';
  }
  const environment =
    import.meta.env.VITE_SENTRY_ENVIRONMENT ?? (import.meta.env.PROD ? 'production' : 'development');
  // Decision: WebView 미니앱은 공식 AppsInToss Sentry 가이드의 첫 tip 박스("Sentry JS 가이드 따르라")
  // 경로를 따르며, 본문의 `@granite-js/plugin-sentry` + `ait sentry upload-sourcemap` 경로는
  // RN 미니앱 전용이라 채택하지 않음. release는 git SHA (sentryVitePlugin이 업로드한
  // 소스맵 키와 일치), dist는 수동 오버라이드(VITE_SENTRY_DIST)가 설정된 경우만.
  // 참고: https://developers-apps-in-toss.toss.im/learn-more/sentry-monitoring.md
  const release = __SENTRY_RELEASE__ || undefined;
  const dist = __SENTRY_DIST__ || undefined;
  const options: BrowserOptions = {
    dsn,
    environment,
    release,
    dist,
    integrations: [
      browserTracingIntegration(),
      // maskAllText + blockAllMedia: PII 보호 기본값 유지 (AppsInToss 심사 전 안전 모드)
      replayIntegration({ maskAllText: true, blockAllMedia: true }),
    ],
    tracesSampleRate: 0.05,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    sendDefaultPii: false,
    beforeSend,
  };
  init(options);
  return 'initialized';
}

export function sendBootHeartbeat(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return;
  captureMessage('boot', 'debug');
}

export { captureException, captureMessage, addBreadcrumb } from '@sentry/browser';
